import React from 'react';
import { Layers, Zap, BarChart3 } from 'lucide-react';
import AdSection from '@/components/AdSection';

const sections = [
  {
    id: 'create',
    icon: Layers,
    tagline: 'Create',
    headline: 'Create interactive ads without the complexity.',
    subheadline: 'Build, customize, and launch high-impact formats in minutes—no code, no heavy production, no outside vendors.',
    ad: 'https://ads.adventive.com/ad?j&pid=2c42c41d-5fd2-4edc-b322-4da3223c721e&type=4&cb=[RANDOM_NUMBER]&click=[CLICK_TRACKER_ENC]&enc=1&gdpr=${GDPR}&gdpr_consent=${GDPR_CONSENT_1268}&fb_url=%%FRAME_BREAK_URL%%&ref=',
    stats: [
      { value: '25+', label: 'Native components' },
      { value: '150+', label: 'Pre-built templates' },
    ],
    features: [
      'Drag-and-drop ad builder',
      'Pre-coded interactive components',
      'No development resources needed',
    ],
    gradient: 'from-[#635bff] to-purple-500',
    bgGradient: 'from-[#635bff]/5 to-purple-500/5',
  },
  {
    id: 'deliver',
    icon: Zap,
    tagline: 'Deliver',
    headline: 'Deliver faster across every ad server.',
    subheadline: 'Seamlessly traffic campaigns across platforms and make mid-flight creative updates without starting over.',
    ad: 'https://ads.adventive.com/ad?j&pid=33e78c9f-3636-423b-8b93-54ce168a2e08&type=4&cb=[RANDOM_NUMBER]&click=[CLICK_TRACKER_ENC]&enc=1&gdpr=${GDPR}&gdpr_consent=${GDPR_CONSENT_1268}&fb_url=%%FRAME_BREAK_URL%%&ref=',
    stats: [
      { value: '20+', label: 'Ad server integrations' },
      { value: 'Real-time', label: 'Creative updates' },
    ],
    features: [
      'Universal ad server compatibility',
      'One-click trafficking',
      'Live creative iteration',
    ],
    gradient: 'from-cyan-500 to-blue-500',
    bgGradient: 'from-cyan-500/5 to-blue-500/5',
  },
  {
    id: 'measure',
    icon: BarChart3,
    tagline: 'Measure',
    headline: 'Results you can feel, not guess.',
    subheadline: 'Track engagement, viewability, and outcomes in one place to prove performance and strengthen every campaign.',
    ad:'https://ads.adventive.com/ad?j&pid=fd70559b-9e2d-4468-b2ea-36bd36b3839e&type=4&cb=[RANDOM_NUMBER]&click=[CLICK_TRACKER_ENC]&enc=1&gdpr=${GDPR}&gdpr_consent=${GDPR_CONSENT_1268}&fb_url=%%FRAME_BREAK_URL%%&ref=',
    stats: [
      { value: '100%', label: 'Transparent metrics' },
      { value: 'Real-time', label: 'Performance data' },
    ],
    features: [
      'Unified performance dashboard',
      'Engagement heatmaps',
      'Viewability tracking',
    ],
    gradient: 'from-emerald-500 to-teal-500',
    bgGradient: 'from-emerald-500/5 to-teal-500/5',
  },
];

interface StorySectionsProps {
  cmsData?: { create?: Record<string, unknown>; deliver?: Record<string, unknown>; measure?: Record<string, unknown> };
}

const StorySections: React.FC<StorySectionsProps> = ({ cmsData }) => {
  // Override section text content from CMS if available
  const getSectionData = (section: typeof sections[0]) => {
    const cms = cmsData?.[section.id as keyof typeof cmsData];
    if (!cms) return section;
    return {
      ...section,
      headline: (cms.headline as string) || section.headline,
      subheadline: (cms.subheadline as string) || section.subheadline,
      features: (cms.features as string[]) || section.features,
      stats: (cms.stats as Array<{ value: string; label: string }>) || section.stats,
      ad: (cms.ad_url as string) || section.ad,
    };
  };
  return (
    <div className="bg-white dark:bg-[#0a0a0f]">
      {sections.map((rawSection, index) => {
        const section = getSectionData(rawSection);
        return (
        <section
          key={section.id}
          className="py-24 lg:py-32 relative overflow-hidden"
        >
          {/* Background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 grid-pattern" />
            {index % 2 === 1 && (
              <div className={`absolute inset-0 bg-gradient-to-br ${section.bgGradient} dark:opacity-50`} />
            )}
            {/* Gradient orb */}
            <div className={`absolute ${index % 2 === 0 ? 'right-0 top-0' : 'left-0 bottom-0'} w-[600px] h-[600px] opacity-20 dark:opacity-30`}>
              <div className={`w-full h-full bg-gradient-to-br ${section.gradient} rounded-full blur-3xl`} />
            </div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${section.gradient} bg-opacity-10 mb-6`}>
                  <section.icon className="w-4 h-4 text-white" />
                  <span className="text-sm font-semibold text-white">{section.tagline}</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
                  {section.headline}
                </h2>
                
                <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                  {section.subheadline}
                </p>

                {/* Features List */}
                <ul className="mt-8 space-y-4">
                  {section.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${section.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-slate-700 dark:text-slate-300 font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Stats */}
                <div className="mt-10 flex gap-10">
                  {section.stats.map((stat, i) => (
                    <div key={i}>
                      <div className={`text-4xl font-extrabold bg-gradient-to-r ${section.gradient} bg-clip-text text-transparent`}>
                        {stat.value}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative">
                  {/* Glow effect */}
                  <div className={`absolute -inset-4 bg-gradient-to-r ${section.gradient} rounded-3xl blur-2xl opacity-20`} />
                  
                  {/* Image container */}
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/20 dark:shadow-black/40 border border-slate-200/50 dark:border-white/10">
                    {section.ad ? (
                      <AdSection adUrl={section.ad} sectionId={section.id} lazy />
                    ) : (
                      <React.Fragment>
                        <img
                          src={section.image}
                          alt={section.headline}
                          className="w-full aspect-[4/3] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
                      </React.Fragment>
                    )}
                    
                    
                    {/* Floating badge */}
                    <div className="absolute top-4 right-4 glass-card px-3 py-1.5 rounded-full">
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Live Ad Preview</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        );
      })}
    </div>
  );
};

export default StorySections;
